import axios, { AxiosInstance } from 'axios'
import type { User, Portfolio, Holding, Goal, PriceAlert, PriceAlertCondition, PriceAlertAction } from '../../../shared/src/types'
import { logger } from '../utils/logger'

class ApiService {
  private api: AxiosInstance
  private token: string | null = null

  constructor() {
    const baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
    
    this.api = axios.create({
      baseURL: `${baseURL}/api`,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Send cookies with requests
      timeout: 15000, // Increased to 15 second timeout
      maxRedirects: 3,
    })

    // Request interceptor to add auth token
    this.api.interceptors.request.use((config) => {
      if (this.token) {
        config.headers.Authorization = `Bearer ${this.token}`
      }
      return config
    })

    // Response interceptor to handle auth errors and retries
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const config = error.config
        
        // Handle auth errors
        if (error.response?.status === 401) {
          // Only redirect to login if this is NOT a login request
          // (to avoid redirect loop on failed login)
          if (!config.url?.includes('/auth/login') && !config.url?.includes('/auth/register')) {
            this.clearAuth()
            window.location.href = '/login' // Redirect to login
          }
          return Promise.reject(error)
        }
        
        // Handle network errors and timeouts with retry
        const shouldRetry = (
          error.code === 'ERR_NETWORK' || 
          error.code === 'ECONNREFUSED' ||
          error.code === 'ECONNABORTED' ||  // Timeout errors
          error.message?.includes('timeout')
        )
        
        if (shouldRetry) {
          config.__retryCount = config.__retryCount || 0
          
          if (config.__retryCount < 3) {
            config.__retryCount += 1
            logger.debug(`API retry attempt ${config.__retryCount}/3 for ${config.url} (${error.code || error.message})`)
            
            // Progressive backoff - wait longer for each retry
            const delay = Math.min(1000 * Math.pow(2, config.__retryCount - 1), 5000)
            await new Promise(resolve => setTimeout(resolve, delay))
            
            return this.api(config)
          } else {
            console.error(`❌ Max retries exceeded for ${config.url}`)
          }
        }
        
        return Promise.reject(error)
      }
    )

    // Load token from localStorage on init
    this.loadToken()
  }

  private loadToken(): void {
    const savedToken = localStorage.getItem('auth_token')
    if (savedToken) {
      this.token = savedToken
    }
  }

  private saveToken(token: string): void {
    this.token = token
    localStorage.setItem('auth_token', token)
  }

  private clearAuth(): void {
    this.token = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
  }

  // Authentication methods
  async register(email: string, password: string, name: string, deviceFingerprint?: string): Promise<{ user: User; token: string; message: string; betaPromo?: boolean }> {
    const response = await this.api.post('/auth/register', { 
      email, 
      password, 
      name,
      deviceFingerprint 
    })
    const { user, token, message, betaPromo } = response.data
    
    this.saveToken(token)
    // Store complete user data including subscription fields
    localStorage.setItem('user', JSON.stringify(user))
    
    return { user, token, message, betaPromo }
  }

  async login(email: string, password: string): Promise<{ user: User; token: string }> {
    const response = await this.api.post('/auth/login', { email, password })
    const { user, token } = response.data
    
    logger.debug('Login response user:', user)
    this.saveToken(token)
    // Store complete user data including isAdmin and subscription fields
    localStorage.setItem('user', JSON.stringify(user))
    
    return { user, token }
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.api.get('/auth/me')
    const user = response.data
    // Update localStorage to keep it in sync
    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
    }
    return user
  }

  async getUserProfile(): Promise<any> {
    const response = await this.api.get('/auth/profile')
    return response.data.data
  }

  async saveUserProfile(profile: any): Promise<any> {
    const response = await this.api.post('/auth/profile', profile)
    return response.data
  }

  async updateUserProfile(profileUpdates: any): Promise<any> {
    const response = await this.api.patch('/auth/profile', profileUpdates)
    return response.data
  }

  // Phone Verification methods
  async sendPhoneVerificationCode(
    phoneNumber: string,
    channel: 'sms' | 'call' = 'sms'
  ): Promise<{ success: boolean; message: string; expiresIn: number }> {
    const response = await this.api.post('/phone/send-code', {
      phoneNumber,
      channel
    })
    return response.data
  }

  async verifyPhoneCode(
    phoneNumber: string,
    code: string,
    userId?: string,
    email?: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post('/phone/verify-code', {
      phoneNumber,
      code,
      userId,
      email
    })
    return response.data
  }

  async resendPhoneCode(
    phoneNumber: string,
    channel: 'sms' | 'call' = 'sms'
  ): Promise<{ success: boolean; message: string; expiresIn: number }> {
    const response = await this.api.post('/phone/resend-code', {
      phoneNumber,
      channel
    })
    return response.data
  }

  logout(): void {
    this.clearAuth()
  }

  // Portfolio methods (single portfolio per user)
  async getPortfolio(): Promise<Portfolio> {
    const response = await this.api.get('/portfolios')
    return response.data
  }

  // Legacy method for backwards compatibility
  async getPortfolios(): Promise<Portfolio[]> {
    const portfolio = await this.getPortfolio()
    return [portfolio]
  }

  async createPortfolio(name: string): Promise<Portfolio> {
    const response = await this.api.post('/portfolios', { name })
    return response.data
  }

  async addHolding(holding: {
    symbol: string
    name: string
    quantity: number
    averageCost: number
    assetType?: string
  }): Promise<Holding> {
    const response = await this.api.post('/portfolios/holdings', holding)
    return response.data
  }

  async updateHolding(holdingId: string, updates: Partial<Holding>): Promise<Holding> {
    const response = await this.api.put(`/portfolios/holdings/${holdingId}`, updates)
    return response.data
  }

  async deleteHolding(holdingId: string): Promise<void> {
    await this.api.delete(`/portfolios/holdings/${holdingId}`)
  }

  async importHoldings(portfolioId: string, holdings: Array<{
    symbol: string
    quantity: number
    averageCost?: number
    name?: string
    assetType?: string
  }>, replaceExisting: boolean = true): Promise<Portfolio> {
    const response = await this.api.post('/portfolios/import', {
      portfolioId,
      holdings,
      replaceExisting
    })
    return response.data
  }

  async getPortfolioHistoricalData(days: number = 30): Promise<any> {
    const response = await this.api.get(`/portfolios/historical/${days}`)
    return response.data
  }

  // Goal methods
  async getGoals(): Promise<Goal[]> {
    const response = await this.api.get('/goals')
    return response.data.data || []
  }

  async getGoal(id: string): Promise<Goal> {
    const response = await this.api.get(`/goals/${id}`)
    return response.data.data
  }

  async createGoal(goalData: {
    name: string
    targetAmount: number
    currentAmount: number
    targetDate: Date
    riskTolerance: 'CONSERVATIVE' | 'MODERATE' | 'AGGRESSIVE'
  }): Promise<Goal> {
    const response = await this.api.post('/goals', goalData)
    return response.data.data
  }

  async updateGoal(id: string, updates: Partial<Goal>): Promise<Goal> {
    const response = await this.api.put(`/goals/${id}`, updates)
    return response.data.data
  }

  async deleteGoal(id: string): Promise<void> {
    await this.api.delete(`/goals/${id}`)
  }

  // Market Sentiment methods
  async getMarketSentiment(portfolioId?: string): Promise<any> {
    const params = portfolioId ? `?portfolioId=${portfolioId}` : ''
    const response = await this.api.get(`/market-sentiment${params}`)
    // Return the nested data if it exists, otherwise return the whole response
    return response.data
  }

  async refreshMarketSentiment(portfolioData?: any): Promise<any> {
    const response = await this.api.post('/market-sentiment/refresh', {
      portfolioData
    })
    return response.data.data || response.data
  }

  async getMarketSentimentHistory(): Promise<any> {
    const response = await this.api.get('/market-sentiment/history')
    return response.data.data || response.data
  }

  // Market Data (HTTP fallback for WebSocket)
  async getMarketData(symbols: string[]): Promise<{ [symbol: string]: any }> {
    const response = await this.api.get(`/market-data/quotes?symbols=${symbols.join(',')}`)
    return response.data.data
  }

  async getQuote(symbol: string): Promise<any> {
    const response = await this.api.get(`/market-data/quote/${symbol}`)
    return response.data.data
  }

  // ML Service methods
  /**
   * Analyze portfolio using ML service
   * @param portfolioId - Portfolio UUID
   * @returns Analysis results from ML service
   */
  async analyzePortfolio(portfolioId: string): Promise<any> {
    try {
      // Use backend proxy endpoint (requires authentication + premium)
      const response = await this.api.post('/ml/analyze', { portfolioId })
      return response.data
    } catch (error) {
      console.error('ML Portfolio Analysis Error:', error)
      throw error
    }
  }

  /**
   * Get ML Recommendations using LSTM correlation model (61.18% accuracy)
   * @param portfolioId - Portfolio UUID
   * @returns LSTM-powered recommendations
   */
  async getMLRecommendationsLSTM(portfolioId: string, count: number = 5): Promise<any> {
    try {
      const response = await this.api.post('/ml/recommendations-lstm', { 
        portfolioId,
        nRecommendations: count
      })
      return response.data
    } catch (error) {
      console.error('LSTM Recommendations Error:', error)
      throw error
    }
  }

  /**
   * Get ML Recommendations (legacy - uses A/B test engines)
   * @deprecated Use getMLRecommendationsLSTM instead
   * @param portfolioId - Portfolio UUID
   * @returns Recommendations from ML service
   */
  async getMLRecommendations(portfolioId: string): Promise<any> {
    try {
      // Use backend proxy endpoint (requires authentication + premium)
      const response = await this.api.post('/ml/recommendations', { portfolioId })
      return response.data
    } catch (error) {
      console.error('ML Recommendations Error:', error)
      throw error
    }
  }

  /**
   * Get portfolio risk alerts
   * @param portfolioId - Portfolio UUID
   * @returns Risk alerts including concentration, sector, and contagion risks
   */
  async getPortfolioRiskAlerts(portfolioId: string): Promise<any> {
    try {
      const response = await this.api.get(`/portfolios/${portfolioId}/risk-alerts`)
      return response.data
    } catch (error) {
      console.error('Risk Alerts Error:', error)
      throw error
    }
  }

  /**
   * Get ML performance metrics (admin only)
   * @param days - Number of days to analyze (7, 30, 90, 180, 365)
   * @returns ML performance data including win rate, returns, and recommendations
   */
  async getMLPerformance(days: number = 30): Promise<any> {
    try {
      const response = await this.api.get(`/ml/performance?days=${days}`)
      return response.data
    } catch (error) {
      console.error('ML Performance Error:', error)
      throw error
    }
  }

  async getMLWinRate(): Promise<any> {
    try {
      const response = await this.api.get('/ml/win-rate')
      return response.data
    } catch (error) {
      console.error('ML Win Rate Error:', error)
      throw error
    }
  }

  /**
   * Get public ML performance metrics (no auth required)
   * @param days - Number of days to analyze (30, 90, 180, 365)
   * @returns Public ML performance data for transparency page
   */
  async getPublicMLPerformance(days: number = 30): Promise<any> {
    try {
      const response = await this.api.get(`/ml/public/performance?days=${days}`)
      return response.data
    } catch (error) {
      console.error('Public ML Performance Error:', error)
      throw error
    }
  }

  /**
   * Get current market regime (bull/bear/sideways)
   * @returns Market regime data with confidence and metrics
   */
  async getMarketRegime(): Promise<any> {
    try {
      const response = await this.api.get('/market/regime')
      return response.data
    } catch (error) {
      console.error('Market Regime Error:', error)
      throw error
    }
  }

  /**
   * Analyze recommendation alignment with market sentiment
   * @param symbol Stock ticker symbol
   * @param action LSTM recommendation action
   * @param confidence LSTM confidence score (0-1)
   * @param marketSentiment Optional market sentiment (will fetch if not provided)
   * @returns Alignment analysis with reasoning and recommendations
   */
  async analyzeRecommendationAlignment(
    symbol: string,
    action: string,
    confidence: number,
    marketSentiment?: string
  ): Promise<any> {
    try {
      const response = await this.api.post('/ml/analyze-alignment', {
        symbol,
        action,
        confidence,
        market_sentiment: marketSentiment
      })
      return response.data
    } catch (error) {
      console.error('Recommendation Alignment Error:', error)
      throw error
    }
  }

  /**
   * Batch analyze alignment for multiple recommendations (more efficient)
   * @param recommendations Array of {symbol, action, confidence}
   * @param marketSentiment Market sentiment (Bullish/Neutral/Bearish)
   * @returns Array of alignment analyses
   */
  async analyzeRecommendationAlignmentBatch(
    recommendations: Array<{ symbol: string; action: string; confidence: number }>,
    marketSentiment: string
  ): Promise<any> {
    try {
      const response = await this.api.post('/ml/analyze-alignment/batch', {
        recommendations,
        market_sentiment: marketSentiment
      })
      return response.data
    } catch (error) {
      console.error('Batch Recommendation Alignment Error:', error)
      throw error
    }
  }

  /**
   * Optimize portfolio using Modern Portfolio Theory
   * @param holdings - Current portfolio holdings
   * @param method - Optimization method (max_sharpe, min_volatility, efficient_return, efficient_risk)
   * @returns Optimization results with efficient frontier and rebalancing trades
   */
  async optimizePortfolio(
    holdings: Holding[], 
    method: string = 'max_sharpe',
    targetReturn?: number,
    targetRisk?: number
  ): Promise<any> {
    try {
      // Use backend proxy endpoint (requires authentication + premium)
      const response = await this.api.post('/ml/optimize-portfolio', { 
        holdings,
        method,
        target_return: targetReturn,
        target_volatility: targetRisk
      }, {
        timeout: 120000 // 2 minute timeout for MPT optimization
      })
      return response.data
    } catch (error) {
      console.error('Portfolio Optimization Error:', error)
      throw error
    }
  }

  // 🚀 Advanced AI Analysis Methods
  /**
   * Get comprehensive AI analysis with all advanced features
   * @param portfolioId - Portfolio UUID
   * @returns Comprehensive AI analysis
   */
  async getComprehensiveAIAnalysis(portfolioId: string): Promise<any> {
    const maxRetries = 2
    let lastError: any
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        logger.debug(`AI Analysis attempt ${attempt}/${maxRetries}`)
        
        // Use backend proxy endpoint (requires authentication + premium)
        const response = await this.api.post('/ml/comprehensive-ai-analysis', 
          { portfolioId },
          { timeout: 150000 } // 150 second timeout for comprehensive AI with FinBERT sentiment
        )
        
        logger.debug('AI Analysis successful')
        return response.data
        
      } catch (error: any) {
        lastError = error
        console.error(`❌ AI Analysis attempt ${attempt} failed:`, error.message)
        
        // Don't retry on non-timeout errors
        if (!error.message?.includes('timeout') && error.code !== 'ECONNABORTED') {
          throw error
        }
        
        // Wait before retry (except on last attempt)
        if (attempt < maxRetries) {
          logger.debug('Waiting before retry...')
          await new Promise(resolve => setTimeout(resolve, 2000 * attempt))
        }
      }
    }
    
    console.error('🚨 All AI Analysis attempts failed')
    throw lastError
  }

  // Advanced ML features should go through backend for security
  // Backend will proxy these requests to ML service with proper authentication

  // AI Health Check - Use backend health endpoint
  async checkAICapabilities(): Promise<any> {
    try {
      // Check backend health which includes ML service status
      const response = await this.api.get('/health')
      return response.data
    } catch (error) {
      console.error('🚀 AI Health Check Error:', error)
      throw error
    }
  }

  // Settings API methods
  async getSettings(): Promise<any> {
    const response = await this.api.get('/settings/profile')
    return response.data.data
  }

  async updateProfile(profileData: { firstName: string; lastName: string; email: string; phone: string }): Promise<any> {
    const response = await this.api.put('/settings/profile', profileData)
    
    // Update localStorage user data immediately
    const storedUser = this.getCurrentUserFromStorage()
    if (storedUser && response.data.data) {
      const updatedUser = { 
        ...storedUser, 
        ...response.data.data // This now includes the 'name' field from backend
      }
      localStorage.setItem('user', JSON.stringify(updatedUser))
    }
    
    return response.data
  }

  async updatePreferences(preferences: { 
    riskTolerance: number; 
    primaryGoal: string; 
    timeHorizon: string; 
    esgFocused: boolean 
  }): Promise<any> {
    const response = await this.api.put('/settings/preferences', preferences)
    return response.data
  }

  async updateNotifications(notifications: { 
    emailPriceAlerts: boolean; 
    emailPortfolioSummary: boolean; 
    emailAIRecommendations: boolean; 
    emailRiskAlerts: boolean;
    emailFrequency: 'realtime' | 'daily' | 'weekly'
  }): Promise<any> {
    const response = await this.api.put('/settings/notifications', notifications)
    return response.data
  }

  async updatePrivacy(privacy: { 
    dataAnalytics: boolean; 
    marketing: boolean 
  }): Promise<any> {
    const response = await this.api.put('/settings/privacy', privacy)
    return response.data
  }

  // Subscription API methods
  async createCheckout(billingCycle: 'monthly' | 'yearly'): Promise<{ checkoutUrl: string }> {
    const response = await this.api.post('/subscription/create-checkout', { billingCycle })
    return response.data
  }

  async getSubscriptionStatus(): Promise<any> {
    const response = await this.api.get('/subscription/status')
    return response.data
  }

  async getSubscriptionPortal(): Promise<any> {
    const response = await this.api.get('/subscription/portal')
    return response.data
  }

  async cancelSubscription(): Promise<any> {
    const response = await this.api.post('/subscription/cancel')
    return response.data
  }

  // Watchlist methods
  async getWatchlist(page?: number, pageSize?: number, sortBy?: string, sortOrder?: string, search?: string): Promise<any> {
    const params = new URLSearchParams()
    if (page) params.append('page', page.toString())
    if (pageSize) params.append('pageSize', pageSize.toString())
    if (sortBy) params.append('sortBy', sortBy)
    if (sortOrder) params.append('sortOrder', sortOrder)
    if (search) params.append('search', search)
    
    const queryString = params.toString()
    const url = queryString ? `/watchlist?${queryString}` : '/watchlist'
    const response = await this.api.get(url)
    return response.data
  }

  async addToWatchlist(symbol: string): Promise<any> {
    const response = await this.api.post('/watchlist', { symbol })
    return response.data
  }

  async removeFromWatchlist(symbol: string): Promise<any> {
    const response = await this.api.delete(`/watchlist/${symbol}`)
    return response.data
  }

  async getPopularSymbols(): Promise<any> {
    const response = await this.api.get('/watchlist/popular')
    return response.data
  }

  // Stock Recommendations (ML-powered)
  async getStockRecommendations(params?: { 
    limit?: number; 
    minConfidence?: number;
    sectors?: string[];
    actions?: string; // Comma-separated: BUY,SELL,HOLD
    fresh?: boolean; // Bypass 30-day frozen portfolio for real-time data
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.minConfidence !== undefined) queryParams.append('minConfidence', params.minConfidence.toString());
    if (params?.sectors?.length) queryParams.append('sectors', params.sectors.join(','));
    if (params?.actions) queryParams.append('actions', params.actions);
    if (params?.fresh) queryParams.append('fresh', 'true');
    
    // Stock recommendations processes 50+ stocks - needs a longer timeout than the global 15s
    const response = await this.api.get(`/stock-recommendations?${queryParams.toString()}`, { timeout: 45000 });
    return response.data;
  }

  /** Start a background LSTM prediction. Returns 202 immediately. */
  async startPredictionAsync(symbol: string): Promise<any> {
    const response = await this.api.post('/ml/predict-async', { symbol });
    return response.data;
  }

  /** Poll for the result of a background prediction started via startPredictionAsync(). */
  async getPredictionStatus(symbol: string): Promise<any> {
    const response = await this.api.get(`/ml/predict-status/${encodeURIComponent(symbol)}`);
    return response.data;
  }

  // Symbol Search methods
  async searchSymbols(query: string): Promise<any> {
    const response = await this.api.get(`/search/symbols?query=${encodeURIComponent(query)}`)
    return response.data.data || []
  }

  // Admin methods
  async getActiveSessions(): Promise<any> {
    const response = await this.api.get('/admin/sessions')
    return response.data
  }

  async terminateSession(sessionId: string): Promise<any> {
    const response = await this.api.delete(`/admin/sessions/${sessionId}`)
    return response.data
  }

  async getAdminDashboard(): Promise<any> {
    const response = await this.api.get('/admin/dashboard')
    return response.data
  }

  // Analytics methods
  async getAnalyticsSummary(): Promise<any> {
    const response = await this.api.get('/analytics/summary')
    return response.data
  }

  async getMyAnalytics(): Promise<any> {
    const response = await this.api.get('/analytics/me')
    return response.data
  }

  // Financial Chat (Claude + MCP integration)
  async sendFinancialChat(message: string, conversationHistory?: Array<{role: string, content: string}>): Promise<any> {
    const response = await this.api.post('/chat/financial', { 
      message,
      conversationHistory: conversationHistory || []
    }, {
      timeout: 120000 // 120s timeout: 90s MCP (model load + data fetch) + 30s Claude processing
    })
    return response
  }

  async getChatLimits(): Promise<any> {
    const response = await this.api.get('/chat/limits')
    return response.data
  }

  // A/B Testing Metrics
  async getABTestMetrics(): Promise<any> {
    const response = await this.api.get('/ml-data/metrics')
    return response.data
  }

  // 💰 Buying Power & Lot Tracking Methods
  async addFunds(portfolioId: string, amount: number, type: 'DEPOSIT' | 'WITHDRAWAL', description?: string): Promise<any> {
    const response = await this.api.post(`/portfolios/${portfolioId}/funds`, {
      amount,
      type,
      description
    })
    return response.data
  }

  async buyShares(portfolioId: string, symbol: string, quantity: number, price: number): Promise<any> {
    const response = await this.api.post(`/portfolios/${portfolioId}/buy`, {
      symbol,
      quantity,
      price
    })
    return response.data
  }

  async sellShares(
    portfolioId: string, 
    symbol: string, 
    quantity: number, 
    price: number,
    lotSelectionMethod?: 'FIFO' | 'LIFO' | 'HIFO' | 'SPECIFIC',
    specificLotIds?: string[]
  ): Promise<any> {
    const response = await this.api.post(`/portfolios/${portfolioId}/sell`, {
      symbol,
      quantity,
      price,
      lotSelectionMethod: lotSelectionMethod || 'FIFO',
      specificLotIds
    })
    return response.data
  }

  async getTransactionHistory(portfolioId: string, limit?: number, offset?: number): Promise<any> {
    const params = new URLSearchParams()
    if (limit) params.append('limit', limit.toString())
    if (offset) params.append('offset', offset.toString())
    
    const queryString = params.toString()
    const url = queryString 
      ? `/portfolios/${portfolioId}/transactions?${queryString}` 
      : `/portfolios/${portfolioId}/transactions`
    
    const response = await this.api.get(url)
    return response.data
  }

  async getLotsForHolding(portfolioId: string, holdingId: string): Promise<any> {
    const response = await this.api.get(`/portfolios/${portfolioId}/holdings/${holdingId}/lots`)
    return response.data
  }

  // Price Alerts
  async getAlerts(activeOnly: boolean = false): Promise<PriceAlert[]> {
    const params = activeOnly ? '?active=true' : ''
    const response = await this.api.get(`/alerts${params}`)
    return response.data
  }

  async getAlert(alertId: string): Promise<PriceAlert> {
    const response = await this.api.get(`/alerts/${alertId}`)
    return response.data
  }

  async createAlert(data: {
    symbol: string
    symbolName?: string
    targetPrice: number
    condition: PriceAlertCondition
    action?: PriceAlertAction
    note?: string
  }): Promise<PriceAlert> {
    const response = await this.api.post('/alerts', data)
    return response.data
  }

  async updateAlert(alertId: string, data: {
    targetPrice?: number
    condition?: PriceAlertCondition
    action?: PriceAlertAction
    isActive?: boolean
    note?: string
  }): Promise<PriceAlert> {
    const response = await this.api.put(`/alerts/${alertId}`, data)
    return response.data
  }

  async deleteAlert(alertId: string): Promise<void> {
    await this.api.delete(`/alerts/${alertId}`)
  }

  // Admin endpoints
  async getAllUsers(): Promise<any> {
    const response = await this.api.get('/admin/users')
    return response.data
  }

  async getUserById(userId: string): Promise<any> {
    const response = await this.api.get(`/admin/users/${userId}`)
    return response.data
  }

  async updateUser(userId: string, updates: {
    name?: string
    firstName?: string
    lastName?: string
    phone?: string
    email?: string
    isAdmin?: boolean
    trialExpiresAt?: string | null
  }): Promise<any> {
    const response = await this.api.patch(`/admin/users/${userId}`, updates)
    return response.data
  }

  async deleteUser(userId: string): Promise<void> {
    await this.api.delete(`/admin/users/${userId}`)
  }

  async getMcpAnalytics(): Promise<any> {
    const response = await this.api.get('/admin/mcp-analytics')
    // Transform snake_case to camelCase for frontend
    const data = response.data
    if (data.requests) {
      data.requests = data.requests.map((req: any) => ({
        ...req,
        userEmail: req.user_email,
        requestMessage: req.request_message,
        responseTimeMs: req.response_time_ms,
        toolsUsed: req.tools_used,
        errorMessage: req.error_message,
        requestDate: req.request_date,
        createdAt: req.created_at
      }))
    }
    return data
  }

  async resetUserPassword(userId: string, newPassword: string): Promise<any> {
    const response = await this.api.post(`/admin/users/${userId}/reset-password`, { newPassword })
    return response.data
  }

  // ML Admin methods
  async refreshMLPredictions(): Promise<any> {
    // Async refresh returns immediately, actual refresh happens in background
    const response = await this.api.post('/admin/ml/refresh-predictions', {}, {
      timeout: 10000 // 10 seconds timeout - response is immediate
    })
    return response.data
  }

  async getPredictionsRanked(params?: {
    limit?: number;
    direction?: 'UP' | 'DOWN' | 'ALL';
    minConfidence?: number;
  }): Promise<any> {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.direction) queryParams.append('direction', params.direction);
    if (params?.minConfidence !== undefined) queryParams.append('minConfidence', params.minConfidence.toString());
    const response = await this.api.get(`/predictions-ranked?${queryParams.toString()}`, { timeout: 15000 });
    return response.data;
  }

  // Email Admin methods
  async sendCustomAdminEmail(recipients: string[], subject: string, htmlContent: string, textContent?: string): Promise<any> {
    const response = await this.api.post('/admin/email/custom', {
      recipients,
      subject,
      htmlContent,
      textContent
    })
    return response.data
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!this.token
  }

  reloadToken(): void {
    this.loadToken()
  }

  getCurrentUserFromStorage(): User | null {
    const userStr = localStorage.getItem('user')
    return userStr ? JSON.parse(userStr) : null
  }

  // Config methods
  async getConfig(): Promise<{ isBeta: boolean; environment: string; features: any }> {
    const response = await this.api.get('/config')
    return response.data
  }

  // ==========================================
  // System Settings
  // ==========================================

  /**
   * Get public system settings (showTransparency flag)
   */
  async getPublicSystemSettings(): Promise<any> {
    try {
      const response = await this.api.get('/system-settings/public')
      return response.data
    } catch (error) {
      console.error('Get Public System Settings Error:', error)
      throw error
    }
  }

  /**
   * Get all system settings (admin only)
   */
  async getSystemSettings(): Promise<any> {
    try {
      const response = await this.api.get('/system-settings')
      return response.data
    } catch (error) {
      console.error('Get System Settings Error:', error)
      throw error
    }
  }

  /**
   * Update system settings (admin only)
   */
  async updateSystemSettings(settings: { showTransparency: boolean }): Promise<any> {
    try {
      const response = await this.api.put('/system-settings', settings)
      return response.data
    } catch (error) {
      console.error('Update System Settings Error:', error)
      throw error
    }
  }

  /**
   * Get subscription mode (admin only)
   */
  async getSubscriptionMode(): Promise<{ mode: 'test' | 'live' }> {
    try {
      const response = await this.api.get('/admin/subscription-mode')
      return response.data
    } catch (error) {
      console.error('Get Subscription Mode Error:', error)
      throw error
    }
  }

  /**
   * Set subscription mode (admin only)
   */
  async setSubscriptionMode(mode: 'test' | 'live'): Promise<{ mode: 'test' | 'live' }> {
    try {
      const response = await this.api.post('/admin/subscription-mode', { mode })
      return response.data
    } catch (error) {
      console.error('Set Subscription Mode Error:', error)
      throw error
    }
  }

  /**
   * API Keys Management
   */
  async getApiKeys(): Promise<{ keys: any[] }> {
    const response = await this.api.get('/api-keys')
    return response.data
  }

  async createApiKey(name: string): Promise<any> {
    const response = await this.api.post('/api-keys', { name })
    return response.data
  }

  async revokeApiKey(keyId: string): Promise<void> {
    await this.api.patch(`/api-keys/${keyId}/revoke`)
  }

  async deleteApiKey(keyId: string): Promise<void> {
    await this.api.delete(`/api-keys/${keyId}`)
  }

  async getApiKeyUsage(keyId: string, days: number = 7): Promise<any> {
    const response = await this.api.get(`/api-keys/usage/${keyId}?days=${days}`)
    return response.data
  }
}

export const apiService = new ApiService()
export default apiService
